/**
 * Study Recommender
 *
 * Detects weaknesses and generates study focus recommendations.
 */

import storageService from '../storage/storageService';
import { DOMAINS } from '../constants/domains';
import { getDomainAccuracyAcrossApps, getDomainReadiness } from './progressAggregator';

/**
 * Detect study weaknesses across all apps.
 * Returns array of alerts sorted by severity.
 */
export function detectWeaknesses() {
  const alerts = [];
  const cards = storageService.get('flashcards', 'all', []);
  const exams = storageService.get('examsim', 'exams', []);
  const today = new Date().toISOString().split('T')[0];

  // 1. Stale domains — no flashcard review in 7+ days
  for (const domain of DOMAINS) {
    const domainCards = cards.filter(c => c.domain === domain.id && c.sr_data.last_review_date);
    if (domainCards.length === 0) continue;

    const lastReview = domainCards
      .map(c => c.sr_data.last_review_date)
      .filter(Boolean)
      .sort()
      .pop();

    if (lastReview) {
      const daysSince = Math.floor(
        (new Date(today) - new Date(lastReview)) / (1000 * 60 * 60 * 24)
      );

      if (daysSince >= 7) {
        alerts.push({
          type: 'stale_domain',
          domainId: domain.id,
          severity: daysSince >= 14 ? 'high' : 'medium',
          message: `No ${domain.name} review in ${daysSince} days`,
          detail: `Your last flashcard review in ${domain.name} was ${daysSince} days ago. Spaced repetition works best with consistent daily review.`,
        });
      }
    }
  }

  // 2. Accuracy drops — compare last 2 exams vs overall average per domain
  if (exams.length >= 3) {
    const domainAcc = getDomainAccuracyAcrossApps();
    const recentExams = exams.slice(-2);

    for (const domain of DOMAINS) {
      const overallAcc = domainAcc[domain.id]?.examAcc || 0;
      if (overallAcc === 0) continue;

      const recentScores = recentExams
        .map(e => e.score?.per_domain?.[domain.id])
        .filter(d => d && d.total > 0)
        .map(d => d.percentage);

      if (recentScores.length === 0) continue;

      const recentAvg = recentScores.reduce((s, v) => s + v, 0) / recentScores.length;
      const drop = overallAcc - recentAvg;

      if (drop >= 15) {
        alerts.push({
          type: 'accuracy_drop',
          domainId: domain.id,
          severity: drop >= 25 ? 'high' : 'medium',
          message: `${domain.name} accuracy dropped ${Math.round(drop)}%`,
          detail: `Your recent exam accuracy in ${domain.name} is ${Math.round(recentAvg)}%, down from your overall ${overallAcc}%. Consider focused review.`,
        });
      }
    }
  }

  // 3. Unseen content — large portions of flashcards never reviewed
  for (const domain of DOMAINS) {
    const domainCards = cards.filter(c => c.domain === domain.id);
    if (domainCards.length < 5) continue;

    const unseenCount = domainCards.filter(c => c.stats.times_seen === 0).length;
    const unseenPct = unseenCount / domainCards.length;

    if (unseenPct > 0.5) {
      alerts.push({
        type: 'unseen_content',
        domainId: domain.id,
        severity: unseenPct > 0.75 ? 'high' : 'medium',
        message: `${Math.round(unseenPct * 100)}% of ${domain.name} cards unseen`,
        detail: `${unseenCount} of ${domainCards.length} flashcards in ${domain.name} have never been reviewed.`,
      });
    }
  }

  // Sort by severity (high first)
  const severityOrder = { high: 0, medium: 1, low: 2 };
  alerts.sort((a, b) => (severityOrder[a.severity] || 2) - (severityOrder[b.severity] || 2));

  return alerts;
}

/**
 * Get today's study recommendation.
 * Returns the domain with lowest (readiness × weight) product.
 */
export function getStudyRecommendation() {
  const readiness = getDomainReadiness();

  let bestTarget = null;
  let lowestScore = Infinity;

  for (const dr of readiness) {
    // Score = readiness adjusted by exam weight (lower = more urgent)
    const urgency = (dr.readiness / 100) * (1 / dr.weight);
    if (urgency < lowestScore) {
      lowestScore = urgency;
      bestTarget = dr;
    }
  }

  if (!bestTarget) {
    return { domain: null, message: 'Start with any domain to begin tracking progress.' };
  }

  const weightPct = Math.round(bestTarget.weight * 100);
  return {
    domain: bestTarget,
    message: `Focus on **${bestTarget.name}** today — your readiness is ${bestTarget.readiness}% in a domain worth ${weightPct}% of the exam.`,
    reason: bestTarget.readiness < 30
      ? 'Low coverage'
      : bestTarget.accuracy < 60
        ? 'Accuracy needs improvement'
        : 'Highest exam weight relative to readiness',
  };
}
